/**
 * Attendance Configuration Mapper
 * Maps transport layer DTOs to domain models
 */

import type {
  LC_AttendanceConfigurationOut,
  LC_AttendanceMarkParameter,
  LC_OvertimeApprovalFlowRole,
  AttendanceConfiguration,
  LC_UpdateAttendanceConfigurationIn,
  LC_UpdateAttendanceMarkParameterIn,
  LC_UpdateOvertimeApprovalFlowRoleIn,
} from '../types';
import {
  ATTENDANCE_MARK_PARAM,
  OVERTIME_FLOW_PROFILE_CODE,
} from '../types';
import type { AttendanceConfigurationFormValues } from '@/schemas/attendance/attendanceConfiguration.schema';


const toBoolean = (value: boolean | null | undefined): boolean =>
  value === true;

const toNumberArray = (value: string | null | undefined): number[] => {
  if (!value) return [];
  return value
    .split(',')
    .map(item => Number(item.trim()))
    .filter(number => !isNaN(number));
};

const getMarkParameterValue = (
  parameters: LC_AttendanceMarkParameter[],
  attributeName: string,
): string | null => {
  const parameter = parameters.find(item => item.attributeName === attributeName);
  return parameter?.value ?? null;
};

const getRoleIdsByProfileCode = (
  roles: LC_OvertimeApprovalFlowRole[],
  profileCode: string,
): number[] =>
  roles
    .filter(role => role.profileCode === profileCode)
    .map(role => role.roleId);

const getFlowProfileIdByProfileCode = (
  roles: LC_OvertimeApprovalFlowRole[],
  profileCode: string,
): number | null => {
  const match = roles.find(role => role.profileCode === profileCode);
  return match?.flowProfileId ?? null;
};

export const mapAttendanceConfiguration = (
  out: LC_AttendanceConfigurationOut,
  markParameters: LC_AttendanceMarkParameter[],
  flowRoles: LC_OvertimeApprovalFlowRole[],
): AttendanceConfiguration => ({
  // General - Marcajes
  geoOfflineRequired: toBoolean(out.offlineGeoLocationRequired),
  collationMarkings: toBoolean(out.lunchBreakMarks),
  geoWebMarking: toBoolean(out.validateGeoLocation),
  disableGeoMarking: toBoolean(out.disableGeoMark),
  geoInconsistencyAlert: toBoolean(out.notifyGeoInconsistencyApi),
  employeeMarkingApproval: toBoolean(out.enableMarkRejection),
  approvalDeadlineHours: out.markRejectionDeadlineHours ?? null,
  todayMarkingModification: toBoolean(out.enableTodayMarkModification),

  // General - Eventos
  eventDefinitionMethod: out.eventDefinitionMethod ?? '',
  entryEvent: out.shiftChangeEntryEventId ?? null,
  exitEvent: out.shiftChangeExitEventId ?? null,
  collationTolerance: out.lunchBreakToleranceMinutes ?? null,
  collationEntryEvent: out.lunchBreakEntryEventId ?? null,
  collationExitEvent: out.lunchBreakExitEventId ?? null,

  // Cálculo - Cortes Mensuales
  multiCutsSameMonth: toBoolean(out.monthlyCut),
  sendByDateConcept: toBoolean(out.sendConceptDetail),
  groupClosure: getMarkParameterValue(markParameters, ATTENDANCE_MARK_PARAM.GROUP_CLOSURE) === '1',

  // Cálculo - Condiciones
  holidaysAsWorkdays: toBoolean(out.holidayAsWorkday),
  art38IgnoreHolidays: toBoolean(out.holidayAsWorkdayArt38),
  calendarScheduleManagement:
    getMarkParameterValue(markParameters, ATTENDANCE_MARK_PARAM.CALENDAR_SCHEDULE_MANAGEMENT) === '1',
  halfDayAbsenteeism: out.halfDayAbsenteeismType != null
    ? String(out.halfDayAbsenteeismType)
    : '',
  calculationByContractEnd:
    getMarkParameterValue(markParameters, ATTENDANCE_MARK_PARAM.CALCULATION_BY_CONTRACT_END) === '1',

  // Notificaciones - Tolerancia
  entryAbsenceMinutes: out.entryMarkToleranceMinutes ?? null,
  exitAbsenceMinutes: out.exitMarkToleranceMinutes ?? null,
  repeatedMarkingWait: out.duplicateRecordToleranceMinutes ?? null,
  absenceValidationControls: toNumberArray(out.absenceByApplication),

  // Notificaciones - Notificaciones
  notifyAbsenceEmployee: toBoolean(out.notifyEmployeeDelay),
  notifyAbsenceSupervisor: toBoolean(out.notifyManagerDelay),
  notifyNoTimeControl: toBoolean(out.notifyAccessControl),
  notifyAbsenceAdmin: toBoolean(out.notifyManagementInsistence),
  adminAbsenceEmail: out.managementInsistenceEmail || '',
  notifyDisconnectedDevice: toBoolean(out.notifyDisconnectedDevice),
  disconnectedDeviceEmail: out.disconnectedDeviceEmail || '',
  markingIncidentRecipient: out.incidentNotificationEmployeeId ?? null,
  notifyEntryDelay: toBoolean(out.notifyEntryDelay),
  entryDelayToleranceMinutes: out.entryDelayNotificationTolerance !== null
    ? Number(out.entryDelayNotificationTolerance)
    : null,
  notifyExitDelay: toBoolean(out.notifyExitDelay),
  exitDelayToleranceMinutes: out.exitDelayNotificationTolerance !== null
    ? Number(out.exitDelayNotificationTolerance)
    : null,
  contractCompanyAbsence: toBoolean(out.contractCompanyAbsence),
  massAttendanceEmail: out.deliveryAlertEmailTo || '',
  dtFiscalizationEmails: out.dtAuditStartNotificationEmails || '',
  excessHoursEmail: out.notifyExcessScheduleEmail || '',

  // Validaciones - Gestor de Marcas
  autoFillHolidays: toBoolean(out.autoFillHolidays),
  autoFillVacations: toBoolean(out.autoFillVacations),
  autoFillSickLeave: toBoolean(out.autoFillSickLeave),
  autoFillSpecialPermissions: toBoolean(out.autoFillSpecialPermissions),
  autoFillAdminPermissions: toBoolean(out.autoFillAdministrativePermissions),
  autoFillFractionPermissions: toBoolean(out.autoFillFractionPermissions),

  // Validaciones - Horas Extras
  approvalFlow: toBoolean(out.overtimeApprovalFlowEnabled),
  reviewerProfiles: getRoleIdsByProfileCode(flowRoles, OVERTIME_FLOW_PROFILE_CODE.REVIEWER),
  approverProfiles: getRoleIdsByProfileCode(flowRoles, OVERTIME_FLOW_PROFILE_CODE.APPROVER),

  // Validaciones - Pacto Compensación
  automaticMode: out.autoCompensationMode || '',
  cutDaysSlack: out.cutToleranceDays ?? null,

  // Metadata for update
  reviewerFlowProfileId:
    getFlowProfileIdByProfileCode(flowRoles, OVERTIME_FLOW_PROFILE_CODE.REVIEWER),
  approverFlowProfileId:
    getFlowProfileIdByProfileCode(flowRoles, OVERTIME_FLOW_PROFILE_CODE.APPROVER),
});

// ─── Reverse mapper (domain → transport) ────────────────────────────────────

export const mapAttendanceConfigurationFormToUpdate = (
  formValues: AttendanceConfigurationFormValues,
  reviewerFlowProfileId: number | null,
  approverFlowProfileId: number | null,
): LC_UpdateAttendanceConfigurationIn => {
  const markParameters: LC_UpdateAttendanceMarkParameterIn[] = [
    {
      attributeName: ATTENDANCE_MARK_PARAM.GROUP_CLOSURE,
      value: formValues.groupClosure ? '1' : '0',
    },
    {
      attributeName: ATTENDANCE_MARK_PARAM.CALENDAR_SCHEDULE_MANAGEMENT,
      value: formValues.calendarScheduleManagement ? '1' : '0',
    },
    {
      attributeName: ATTENDANCE_MARK_PARAM.CALCULATION_BY_CONTRACT_END,
      value: formValues.calculationByContractEnd ? '1' : '0',
    },
  ];

  const overtimeApprovalFlowRoles: LC_UpdateOvertimeApprovalFlowRoleIn[] = [];
  if (reviewerFlowProfileId !== null) {
    overtimeApprovalFlowRoles.push({
      flowProfileId: reviewerFlowProfileId,
      roleIds: formValues.reviewerProfiles,
    });
  }
  if (approverFlowProfileId !== null) {
    overtimeApprovalFlowRoles.push({
      flowProfileId: approverFlowProfileId,
      roleIds: formValues.approverProfiles,
    });
  }

  return {
    configuration: {
      offlineGeoLocationRequired: formValues.geoOfflineRequired,
      lunchBreakMarks: formValues.collationMarkings,
      validateGeoLocation: formValues.geoWebMarking,
      disableGeoMark: formValues.disableGeoMarking,
      notifyGeoInconsistencyApi: formValues.geoInconsistencyAlert,
      enableMarkRejection: formValues.employeeMarkingApproval,
      markRejectionDeadlineHours: formValues.approvalDeadlineHours,
      enableTodayMarkModification: formValues.todayMarkingModification,
      eventDefinitionMethod: formValues.eventDefinitionMethod || null,
      shiftChangeEntryEventId: formValues.entryEvent,
      shiftChangeExitEventId: formValues.exitEvent,
      lunchBreakToleranceMinutes: formValues.collationTolerance ?? 0,
      lunchBreakEntryEventId: formValues.collationEntryEvent,
      lunchBreakExitEventId: formValues.collationExitEvent,
      monthlyCut: formValues.multiCutsSameMonth,
      sendConceptDetail: formValues.sendByDateConcept,
      holidayAsWorkday: formValues.holidaysAsWorkdays,
      holidayAsWorkdayArt38: formValues.art38IgnoreHolidays,
      halfDayAbsenteeismType: formValues.halfDayAbsenteeism !== '' ? Number(formValues.halfDayAbsenteeism) : 0,
      entryMarkToleranceMinutes: formValues.entryAbsenceMinutes,
      exitMarkToleranceMinutes: formValues.exitAbsenceMinutes,
      duplicateRecordToleranceMinutes: formValues.repeatedMarkingWait ?? 0,
      absenceByApplication: formValues.absenceValidationControls.length > 0
        ? formValues.absenceValidationControls.join(',')
        : null,
      notifyEmployeeDelay: formValues.notifyAbsenceEmployee,
      notifyManagerDelay: formValues.notifyAbsenceSupervisor,
      notifyAccessControl: formValues.notifyNoTimeControl,
      notifyManagementInsistence: formValues.notifyAbsenceAdmin,
      managementInsistenceEmail: formValues.adminAbsenceEmail || null,
      notifyDisconnectedDevice: formValues.notifyDisconnectedDevice,
      disconnectedDeviceEmail: formValues.disconnectedDeviceEmail || null,
      incidentNotificationEmployeeId: formValues.markingIncidentRecipient,
      notifyEntryDelay: formValues.notifyEntryDelay,
      entryDelayNotificationTolerance: formValues.entryDelayToleranceMinutes !== null
        ? String(formValues.entryDelayToleranceMinutes)
        : null,
      notifyExitDelay: formValues.notifyExitDelay,
      exitDelayNotificationTolerance: formValues.exitDelayToleranceMinutes !== null
        ? String(formValues.exitDelayToleranceMinutes)
        : null,
      contractCompanyAbsence: formValues.contractCompanyAbsence,
      bulkActionEmailAddress: formValues.massAttendanceEmail || null,
      dtAuditStartNotificationEmails: formValues.dtFiscalizationEmails || null,
      autoFillHolidays: formValues.autoFillHolidays,
      autoFillVacations: formValues.autoFillVacations,
      autoFillSickLeave: formValues.autoFillSickLeave,
      autoFillSpecialPermissions: formValues.autoFillSpecialPermissions,
      autoFillAdministrativePermissions: formValues.autoFillAdminPermissions,
      autoFillFractionPermissions: formValues.autoFillFractionPermissions,
      overtimeApprovalFlowEnabled: formValues.approvalFlow,
      autoCompensationMode: formValues.automaticMode || null,
      cutToleranceDays: formValues.cutDaysSlack,
      notifyExcessScheduleEmail: formValues.excessHoursEmail || null,
    },
    attendanceMarkParameters: markParameters,
    overtimeApprovalFlowRoles:
      overtimeApprovalFlowRoles.length > 0 ? overtimeApprovalFlowRoles : null,
  };
};
